import {
  useComments,
  useAddComment,
  useEditComment,
  useDeleteComment,
  mapCommentDtoToEnhancedComment,
  CommentsSortOrder,
  CommentsSortBy,
  CommentsCategory
} from '@domain/comments'
import {DataContentWrapper} from '@hconnect/common/containers'
import {headlineSx} from '@hconnect/common/styles'
import {useUserContext} from '@hconnect/login'
import {CardSectionTitle} from '@hconnect/uikit/src/lib2'
import {ControlledCommentBox, ControlledCommentProps} from '@hm/fundamental'
import {Box, Divider} from '@mui/material'
import {useState, FC} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {usePlantUpmId, useTimeZone} from '../../hooks'

type ActionCommentsProps = {
  actionId: string
}

const QUERY_KEY_PREFIX = 'pom-actions'
const CATEGORY_INSTANCE_ID_PREFIX = 'pom-action-comments'

export const ActionComments: FC<ActionCommentsProps> = ({actionId}) => {
  const {t, i18n} = useTranslation(FEATURE_NAMESPACE)
  const language = i18n.language
  const plantId = usePlantUpmId()
  const timezone = useTimeZone()
  const {loggedUser} = useUserContext()
  const [commentInputValue, setCommentInputValue] = useState('')

  const commentCategoryInstanceId = `${CATEGORY_INSTANCE_ID_PREFIX}-${plantId}-${actionId}`
  const commentsCategory = CommentsCategory.POMActions
  const commentsSortOrder = CommentsSortOrder.Descending
  const commentsSortBy = CommentsSortBy.CreatedOn

  const {
    data: comments,
    isLoading,
    isError,
    isRefetching,
    refetch
  } = useComments(
    {
      plantId,
      commentCategoryInstanceIds: [commentCategoryInstanceId],
      commentsCategory,
      commentsSortOrder,
      commentsSortBy
    },
    QUERY_KEY_PREFIX
  )

  const {mutateAsync: editComment} = useEditComment({plantId, commentsCategory}, QUERY_KEY_PREFIX)

  const {mutateAsync: addComment} = useAddComment({plantId, commentsCategory}, QUERY_KEY_PREFIX)

  const {mutateAsync: deleteComment} = useDeleteComment(
    {plantId, commentsCategory},
    QUERY_KEY_PREFIX
  )

  const handleDeleteComment = async (id: string) => {
    await deleteComment({id, commentCategoryInstanceId})
  }

  const handleAddComment = async (comment: string) => {
    await addComment({value: comment, commentCategoryInstanceId})
    setCommentInputValue('')
  }

  const handleEditComment = async ({id}: ControlledCommentProps<string>, value: string) => {
    await editComment({value, id, commentCategoryInstanceId})
  }

  const enhancedComments =
    comments?.map((comment) =>
      mapCommentDtoToEnhancedComment(comment, timezone, language, loggedUser?.name)
    ) ?? []

  return (
    <DataContentWrapper
      showLoader={isLoading}
      isError={isError}
      isNoData={false}
      isRefetching={isRefetching}
      retryFunction={refetch}
      progressSx={{color: 'primary.main'}}
    >
      <Box>
        <CardSectionTitle sx={headlineSx()}>
          {t('comments.cardTitle')} ({enhancedComments.length})
        </CardSectionTitle>
        <Divider sx={{borderColor: 'divider', mb: 1.5}} />
        <ControlledCommentBox
          comments={enhancedComments}
          commentInputValue={commentInputValue}
          onCommentInputChange={setCommentInputValue}
          onDelete={handleDeleteComment}
          onEdit={handleEditComment}
          onSubmit={handleAddComment}
        />
      </Box>
    </DataContentWrapper>
  )
}
